import React, { ReactElement } from 'react'
import styled from 'styled-components'
import { Tooltip } from '@material-ui/core'
import { useSnackbar } from 'notistack'

// The admin tables carry values long enough to set a whole row's height on their own:
// file names, email subjects, composite container numbers, failure explanations. They are
// cut to one line here. Nothing is lost - the full text is in the tooltip and one click
// away from the clipboard, which is what these values are usually wanted for (pasting a
// subject into a mail search, a container number into a ticket).
//
// The width comes from the cell by default. A pixel $width would make the column a floor
// the table cannot shrink below, which is what put a horizontal scrollbar under these
// tables in the first place.
const Clamped = styled.span<{ $width?: number }>`
  display: block;
  min-width: 0;
  max-width: ${(props) => (props.$width ? `${props.$width}px` : '100%')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

// Clamped text whose only click action is copying itself - nothing underneath it competes
// for the click, so the whole line is the copy target.
const CopyableText = styled(Clamped)`
  cursor: pointer;
`

const TooltipText = styled.div`
  cursor: pointer;
  font-size: 0.75rem;
  line-height: 1.4;
  word-break: break-word;
`
const TooltipHint = styled.div`
  margin-top: 4px;
  font-size: 0.65rem;
  opacity: 0.7;
`

const useCopy = () => {
  const { enqueueSnackbar } = useSnackbar()

  return (text: string) => {
    navigator.clipboard.writeText(text)
    enqueueSnackbar('Copied to clipboard')
  }
}

interface Props {
  text: string
  children: ReactElement
}

// Hovering a clamped value shows the whole of it; the tooltip itself is clickable so the
// text can be copied even where the cell is a link and the click belongs to the link.
const FullText: React.FC<Props> = ({ text, children }) => {
  const copy = useCopy()

  return (
    <Tooltip
      interactive
      placement='top-start'
      title={
        <TooltipText onClick={() => copy(text)}>
          {text}
          <TooltipHint>Click to copy</TooltipHint>
        </TooltipText>
      }
    >
      {children}
    </Tooltip>
  )
}

export { Clamped, CopyableText, FullText, useCopy }
